import { useId } from 'react';

// ─── Helpers de UI ────────────────────────────────────────────────────────
export function NumInput({ label, value, onChange, prefix = '$', suffix = '', min = 0, step = 1, hint }:
  { label: string; value: number; onChange: (v: number) => void; prefix?: string; suffix?: string; min?: number; step?: number | 'any'; hint?: string }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</label>
      <div className="flex items-center gap-1 rounded border border-slate-200 bg-white px-3 py-2 focus-within:border-brand-400 focus-within:ring-1 focus-within:ring-brand-200 transition-all">
        {prefix && <span className="text-slate-400 text-sm select-none">{prefix}</span>}
        <input id={id} type="number" min={min} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 min-w-0 bg-transparent text-sm text-slate-800 font-mono focus:outline-none" />
        {suffix && <span className="text-slate-400 text-sm select-none">{suffix}</span>}
      </div>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

// ─── Semáforo de rentabilidad ─────────────────────────────────────────────
// Rentabilidad neta (lo que queda en el bolsillo, % de lo que costó) contra la deseada.
export type TonoRentabilidad = 'verde' | 'amarillo' | 'rojo';

export function tonoRentabilidad(netaPct: number, objetivoPct: number): TonoRentabilidad {
  if (netaPct < 0) return 'rojo';
  return netaPct >= objetivoPct ? 'verde' : 'amarillo';
}

export const AYUDA_SEMAFORO = 'Rentabilidad neta: lo que te queda en el bolsillo, como % de lo que costó. '
  + 'Verde: llega a la rentabilidad deseada. Amarillo: gana, pero menos que la deseada. Rojo: pierde plata.';

const SEMAFORO: Record<TonoRentabilidad, { clase: string; punto: string; texto: string }> = {
  verde: { clase: 'bg-emerald-50 text-emerald-700', punto: 'bg-emerald-500', texto: 'llega a la deseada' },
  amarillo: { clase: 'bg-amber-50 text-amber-700', punto: 'bg-amber-500', texto: 'menos que la deseada' },
  rojo: { clase: 'bg-red-50 text-red-700', punto: 'bg-red-500', texto: 'pierde plata' },
};

export function SemaforoRentabilidad({ netaPct, objetivoPct }: { netaPct: number; objetivoPct: number }) {
  const s = SEMAFORO[tonoRentabilidad(netaPct, objetivoPct)];
  const pct = `${netaPct.toLocaleString('es-AR', { maximumFractionDigits: 2 })}%`;
  return (
    <span title={AYUDA_SEMAFORO} aria-label={`Rentabilidad neta ${pct}: ${s.texto}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${s.clase}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.punto}`} aria-hidden="true" />
      {pct}
    </span>
  );
}
